import * as listService from './service';

/**
 * CRM List Management State Structure
 * 
 * Domain Logic:
 * - Organizes lists by CRM entity type (contacts, accounts, opportunities)
 * - Tracks default list preferences per entity for user convenience
 * - Manages loading states and error handling for list operations
 * - Maintains list updates and synchronization across entity types
 * - Handles list CRUD operations with proper state immutability
 */

/**
 * Initial state for CRM list management
 * @type {Object}
 * @property {Object} listsByEntity - Lists organized by entity type
 * @property {Object} defaultIds - Default list IDs by entity type
 * @property {boolean} loading - Loading state for list operations
 * @property {string|null} error - Error message from list operations
 */
const initialState = {
  listsByEntity: {}, // { [vendorEntityType]: [lists] }
  defaultIds: {},   // { [vendorEntityType]: listId }
  loading: false,
  error: null,
};

/**
 * CRM List Management Reducer
 * 
 * Handles state updates for:
 * - Loading lists for specific CRM entities
 * - Setting default list preferences per entity
 * - Updating list configurations and metadata
 * - Managing loading states and error handling
 * 
 * @param {Object} state - Current state
 * @param {Object} action - Redux action
 * @returns {Object} Updated state
 */
export default function listsReducer(state = initialState, action) {
  switch (action.type) {
    /**
     * Sets loading state and clears any previous errors
     */
    case 'LISTS_LOADING':
      return { ...state, loading: true, error: null };
      
    /**
     * Stores loaded lists for a specific CRM entity type
     * Organizes lists by entity for efficient access
     */
    case 'LISTS_LOADED':
      return {
        ...state,
        loading: false,
        listsByEntity: {
          ...state.listsByEntity,
          [action.entityType]: action.data,
        }
      };
      
    /**
     * Processes default lists and builds entity-to-default mapping
     * Creates lookup table for user's preferred list per entity
     */
    case 'DEFAULT_LISTS_LOADED': {
      // Build default list mapping from loaded data
      const newDefaultIds = { ...state.defaultIds };
      action.data.forEach(list => {
        newDefaultIds[list.entity_type] = list.id;
      });
      return {
        ...state,
        loading: false,
        defaultIds: newDefaultIds,
      };
    }
    
    /**
     * Handles error states from list operations
     * Clears loading state and stores error message
     */
    case 'LISTS_ERROR':
      return { ...state, loading: false, error: action.error };
      
    /**
     * Updates default list preference for a specific entity type
     * Maintains user's choice of which list to show by default
     */
    case 'SET_DEFAULT_ID':
      return {
        ...state,
        defaultIds: {
          ...state.defaultIds,
          [action.entityType]: action.listId,
        }
      };
      
    /**
     * Updates existing list or adds new list to entity collection
     * Handles list modifications while preserving entity organization
     */
    case 'UPDATE_LIST': {
      // Update existing list or add new one to entity collection
      const lists = state.listsByEntity[action.entityType] || [];
      const idx = lists.findIndex(l => l.id === action.list.id);
      let newLists;
      if (idx !== -1) {
        newLists = [...lists];
        newLists[idx] = action.list;
      } else {
        newLists = [...lists, action.list];
      }
      return {
        ...state,
        listsByEntity: {
          ...state.listsByEntity,
          [action.entityType]: newLists,
        }
      };
    }
    
    default:
      return state;
  }
} 